/** Secção «Explore mais» configurável no CMS (powers e aces). */

export const RELATED_EXPLORE_MAX_ITEMS = 4;

export type RelatedExploreConfig = {
  /** Título da secção; vazio → título por omissão na UI. */
  title?: string;
  /** Nomes ou slugs (máx. 4). */
  items: string[];
};

export function emptyRelatedExplore(): RelatedExploreConfig {
  return { title: '', items: [] };
}

export function normalizeRelatedExplore(
  raw: RelatedExploreConfig | null | undefined
): RelatedExploreConfig {
  if (!raw || typeof raw !== 'object') return emptyRelatedExplore();
  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  const items = Array.isArray(raw.items)
    ? raw.items
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
        .slice(0, RELATED_EXPLORE_MAX_ITEMS)
    : [];
  return { title, items };
}

/** Normaliza para persistência (sempre objecto — permite limpar no backend). */
export function relatedExploreForPersist(
  raw: RelatedExploreConfig | null | undefined
): RelatedExploreConfig {
  return normalizeRelatedExplore(raw);
}

export function findByNameOrSlug<T extends { slug: string; name: string }>(
  catalog: T[],
  query: string
): T | undefined {
  const q = query.trim().toLowerCase();
  if (!q) return undefined;

  const keyOf = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');

  const qKey = keyOf(query);

  const exact =
    catalog.find((item) => item.slug.toLowerCase() === q) ??
    catalog.find((item) => item.name.toLowerCase() === q);
  if (exact) return exact;

  const byKey = catalog.find(
    (item) => keyOf(item.slug) === qKey || keyOf(item.name) === qKey
  );
  if (byKey) return byKey;

  const starts = catalog.filter(
    (item) =>
      item.slug.toLowerCase().startsWith(q) ||
      item.name.toLowerCase().startsWith(q) ||
      keyOf(item.slug).startsWith(qKey) ||
      keyOf(item.name).startsWith(qKey)
  );
  if (starts.length === 1) return starts[0];

  return undefined;
}

/**
 * Resolve itens manuais na ordem pedida; ignora o item actual e entradas inválidas.
 * Se `items` estiver vazio, devolve `null` para o caller aplicar o fallback automático.
 */
export function resolveRelatedByItems<T extends { slug: string; name: string }>(
  items: string[] | undefined,
  catalog: T[],
  currentSlug: string,
  max = RELATED_EXPLORE_MAX_ITEMS
): T[] | null {
  const queries = (items ?? []).map((s) => s.trim()).filter(Boolean);
  if (queries.length === 0) return null;

  const seen = new Set<string>();
  const current = currentSlug.trim().toLowerCase();
  const out: T[] = [];

  for (const query of queries) {
    if (out.length >= max) break;
    const found = findByNameOrSlug(catalog, query);
    if (!found) continue;
    const key = found.slug.toLowerCase();
    if (key === current || seen.has(key)) continue;
    seen.add(key);
    out.push(found);
  }

  return out;
}
